import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ModelRoute, ModelRouteBoundCapability } from "@zworks/console-contracts";
import { Cpu, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError, createModelRoute, modelRoutesQuery } from "@/lib/api";

const boundCapabilities: { value: ModelRouteBoundCapability; label: string }[] = [
  { value: "hard_bound", label: "Hard bound (enforce budget)" },
  { value: "soft_bound", label: "Soft bound (warn at budget)" },
  { value: "disabled", label: "Disabled" },
] as const;

export function ModelRoutesSection({
  projectId,
  role,
}: {
  projectId: string;
  role: "owner" | "developer" | "approver" | "viewer";
}) {
  const queryClient = useQueryClient();
  const routes = useQuery(modelRoutesQuery(projectId));
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<ModelRoute | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure] = useState<ApiError | null>(null);
  const canManage = role === "owner";

  // failure state is used in ErrorState for display; the setter isn't used directly
  // but is kept for type consistency with the component

  async function refresh(message: string) {
    setNotice(message);
    await queryClient.invalidateQueries({ queryKey: ["projects", projectId, "model-routes"] });
  }

  if (role === "viewer") {
    return (
      <ForbiddenState
        title="Model routes are only visible to owners"
        description="Ask a project owner if you need these details."
      />
    );
  }

  if (routes.isPending) return <LoadingState label="Loading model routes" rows={4} />;
  if (routes.isError)
    return (
      <ErrorState
        title="Model routes could not be loaded"
        description="The request could not be completed."
        requestId={failure instanceof ApiError ? (failure.requestId ?? "unknown") : "unknown"}
        onRetry={() => void routes.refetch()}
      />
    );

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">Model routes</h2>
        <div className="flex items-center gap-3">
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Cpu className="size-4" aria-hidden />
            BYOK routes for model providers
          </p>
          {canManage ? (
            <Button
              onClick={() => {
                setNotice(null);
                setCreating(true);
              }}
            >
              <Plus className="size-4" aria-hidden />
              Add model route
            </Button>
          ) : null}
        </div>
      </header>
      {notice ? (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      ) : null}
      {failure ? (
        <p role="alert" className="text-sm text-danger">
          {failure.problem?.detail ?? failure.message}
          {failure.requestId ? ` Reference ${failure.requestId}.` : ""}
        </p>
      ) : null}
      <section aria-label="Project model routes">
        <h3 className="text-sm text-muted-foreground">Project model routes</h3>
        {!routes.data?.length ? (
          <EmptyState
            title="No model routes configured"
            description="Add a model route to connect a BYOK credential to a provider and model."
          />
        ) : (
          routes.data.map((route) => (
            <article
              key={route.id}
              aria-label={route.name}
              className="flex flex-wrap items-center gap-3 border-b py-4"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{route.name}</p>
                <p className="text-sm text-muted-foreground">
                  {route.provider} / {route.model} · {route.pricingVersion}
                </p>
                <p className="text-xs text-muted-foreground">
                  Credential ref <span className="font-mono">{route.credentialRefId}</span>
                </p>
              </div>
              <Badge
                variant={
                  route.boundCapability === "hard_bound"
                    ? "success"
                    : route.boundCapability === "soft_bound"
                      ? "warning"
                      : "danger"
                }
              >
                {boundCapabilities.find((b) => b.value === route.boundCapability)?.label ??
                  route.boundCapability}
              </Badge>
              {canManage ? (
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={`Delete ${route.name}`}
                  onClick={() => setDeleting(route)}
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              ) : null}
            </article>
          ))
        )}
      </section>
      {creating ? (
        <CreateDialog
          projectId={projectId}
          close={() => setCreating(false)}
          done={(message) => void refresh(message)}
        />
      ) : null}
      {deleting ? (
        <DeleteDialog
          route={deleting}
          close={() => setDeleting(null)}
          done={(message) => void refresh(message)}
        />
      ) : null}
    </div>
  );
}

function CreateDialog({
  projectId,
  close,
  done,
}: {
  projectId: string;
  close: () => void;
  done: (message: string) => void;
}) {
  const [name, setName] = useState("");
  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  const [pricingVersion, setPricingVersion] = useState("");
  const [credentialRefId, setCredentialRefId] = useState("");
  const [boundCapability, setBoundCapability] = useState<ModelRouteBoundCapability>("hard_bound");
  // Type assertion to fix onValueChange type mismatch
  const handleBoundCapabilityChange = (value: ModelRouteBoundCapability) =>
    setBoundCapability(value);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (
      !name.trim() ||
      !provider.trim() ||
      !model.trim() ||
      !pricingVersion.trim() ||
      !credentialRefId.trim() ||
      saving
    )
      return;
    setSaving(true);
    setFailure(null);
    try {
      await createModelRoute(projectId, {
        name: name.trim(),
        provider: provider.trim(),
        model: model.trim(),
        pricingVersion: pricingVersion.trim(),
        credentialRefId: credentialRefId.trim(),
        boundCapability,
      });
      done("Model route created.");
      close();
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? (error.problem?.detail ?? error.message)
          : "The model route could not be created.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !saving) close();
      }}
    >
      <DialogContent className="w-[min(36rem,calc(100%-2rem))]">
        <DialogTitle>Add model route</DialogTitle>
        <DialogDescription>
          Create a project-scoped route binding a provider/model to a BYOK credential. The
          credential must be an active
          <code>model-key</code> credential for the same provider.
        </DialogDescription>
        <form onSubmit={submit} className="mt-4 flex flex-col gap-4">
          <label className="text-sm font-medium">
            Name
            <Input
              required
              maxLength={80}
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={saving}
              placeholder="e.g., claude-opus"
            />
          </label>
          <label className="text-sm font-medium">
            Provider
            <Input
              required
              value={provider}
              onChange={(event) => setProvider(event.target.value)}
              disabled={saving}
              placeholder="anthropic"
            />
          </label>
          <label className="text-sm font-medium">
            Model
            <Input
              required
              value={model}
              onChange={(event) => setModel(event.target.value)}
              disabled={saving}
              placeholder="claude-opus-5"
            />
          </label>
          <label className="text-sm font-medium">
            Pricing version
            <Input
              required
              value={pricingVersion}
              onChange={(event) => setPricingVersion(event.target.value)}
              disabled={saving}
              placeholder="2026-01-01"
            />
          </label>
          <label className="text-sm font-medium">
            Credential reference ID
            <Input
              required
              value={credentialRefId}
              onChange={(event) => setCredentialRefId(event.target.value)}
              disabled={saving}
              placeholder="cred_..."
            />
          </label>
          <label className="text-sm font-medium">
            Bound capability
            <Select
              value={boundCapability}
              onValueChange={handleBoundCapabilityChange}
              disabled={saving}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select bound capability" />
              </SelectTrigger>
              <SelectContent>
                {boundCapabilities.map((b) => (
                  <SelectItem key={b.value} value={b.value}>
                    {b.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          {failure ? (
            <p role="alert" className="text-sm text-danger">
              {failure}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button variant="outline" type="button" disabled={saving} onClick={close}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !name.trim() ||
                !provider.trim() ||
                !model.trim() ||
                !pricingVersion.trim() ||
                !credentialRefId.trim() ||
                saving
              }
            >
              {saving ? "Creating…" : "Create model route"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({
  route,
  close,
  done,
}: {
  route: ModelRoute;
  close: () => void;
  done: (message: string) => void;
}) {
  const [saving, setSaving] = useState(false);

  async function onDelete() {
    setSaving(true);
    try {
      // The hub API doesn't have a delete endpoint yet, but we'll call it when it does
      // For now, just close and show a message
      done("Delete not yet implemented on hub API.");
      close();
    } catch {
      // Error handling would go here
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !saving) close();
      }}
    >
      <DialogContent>
        <DialogTitle>Delete model route</DialogTitle>
        <DialogDescription>
          Delete <strong>{route.name}</strong> ({route.provider}/{route.model})? This cannot be
          undone.
        </DialogDescription>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" disabled={saving} onClick={close}>
            Cancel
          </Button>
          <Button disabled={saving} variant="destructive" onClick={onDelete}>
            {saving ? "Deleting…" : "Delete model route"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
